import React, { useState } from 'react';
import { Globe, Tag, ExternalLink, Eye, EyeOff, Copy, CheckCircle } from 'lucide-react';
import { GeneratedDomain } from '../services/api';

interface GeneratedDomainsProps {
  domains: GeneratedDomain[];
  selectedTerms: string[];
}

const GeneratedDomains: React.FC<GeneratedDomainsProps> = ({ domains, selectedTerms }) => {
  const [showKeywords, setShowKeywords] = useState(false);
  const [copiedDomain, setCopiedDomain] = useState<string | null>(null);

  const handleCopyDomain = async (domain: string) => {
    try {
      await navigator.clipboard.writeText(domain);
      setCopiedDomain(domain);
      setTimeout(() => setCopiedDomain(null), 2000);
    } catch (err) {
      console.error('Failed to copy domain:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Globe className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Generated Domains</h2>
              <p className="text-gray-600">
                {domains.length} domains generated from {selectedTerms.length} selected terms
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowKeywords(!showKeywords)}
            className="flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700"
          >
            {showKeywords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span>{showKeywords ? 'Hide' : 'Show'} keywords</span>
          </button>
        </div>
      </div>

      {/* Selected Terms Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Tag className="h-5 w-5 mr-2" />
          Selected Terms
        </h3>
        <div className="flex flex-wrap gap-2">
          {selectedTerms.map((term, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
            >
              {term}
            </span>
          ))}
        </div>
      </div>

      {/* Generated Domains Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Domain
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                {showKeywords && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Keywords
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {domains.map((generatedDomain, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <Globe className="h-4 w-4 text-green-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {generatedDomain.domain}
                        </div>
                        <div className="text-sm text-gray-500">
                          TLD: {generatedDomain.domain.split('.').pop()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs">
                      {generatedDomain.description}
                    </div>
                  </td>
                  {showKeywords && (
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {generatedDomain.keywords.map((keyword, keywordIndex) => (
                          <span
                            key={keywordIndex}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCopyDomain(generatedDomain.domain)}
                        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        title="Copy domain"
                      >
                        {copiedDomain === generatedDomain.domain ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                      <button
                        onClick={() => window.open(`/dashboard/domains/${generatedDomain.domain}`, '_blank')}
                        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        title="View domain details"
                      >
                        <Eye className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => window.open(`https://${generatedDomain.domain}`, '_blank')}
                        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        title="Visit domain website"
                      >
                        <ExternalLink className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{domains.length}</div>
            <div className="text-sm text-gray-600">Total Domains</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {domains.reduce((total, domain) => total + domain.keywords.length, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Keywords</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{selectedTerms.length}</div>
            <div className="text-sm text-gray-600">Selected Terms</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratedDomains; 